import { niceTicks } from './niceTicks';
import type { SavingsRateModel } from './savingsRate';

/** The savings rate a month should reach, in percent, as the landing's chart sets it. */
export const SAVINGS_GOAL = 20;

// The landing's scale: ticks at 0, 10, 20 and 30 %, room up to 35 %.
const FLOOR_TOP = 30;

export interface SavingsScale {
  /** Labelled rates, from zero up. */
  ticks: number[];
  hi: number;
}

/**
 * The rates the chart spans above zero: the landing's scale, growing past 30 % only
 * when a month saved more.
 */
export function savingsScale(rates: (number | null)[]): SavingsScale {
  const best = Math.max(FLOOR_TOP, ...rates.filter((r): r is number => r !== null));
  const ticks = niceTicks(0, best, 3);
  const step = ticks[1] - ticks[0];
  return { ticks, hi: Math.max(ticks[ticks.length - 1] + step / 2, best * 1.1) };
}

/** Room under the zero line, kept only while a month is overspent. */
export const SAVINGS_DIP = 44;
// A small overspend still needs a mark that reads as one.
const DIP_MIN = 6;

/**
 * How far an overspent month reaches under the zero line, in pixels: on the same
 * scale as the bars above it (`pxPerPoint`), up to the room kept under the line,
 * where a deeper month stops and its label names the rate.
 */
export const savingsDip = (pct: number, pxPerPoint: number) =>
  Math.min(SAVINGS_DIP, Math.max(DIP_MIN, -pct * pxPerPoint));

export interface SavingsSummary {
  latest: SavingsRateModel['bars'][number];
  /** What the latest month kept; negative when it overspent. */
  saved: number;
  monthsOverGoal: number;
}

/** The latest month against the goal, and how many months of the period reached it. */
export function savingsSummary(bars: SavingsRateModel['bars']): SavingsSummary {
  const latest = bars[bars.length - 1];
  return {
    latest,
    saved: latest.income - latest.expenses,
    monthsOverGoal: bars.filter((b) => b.pct !== null && b.pct >= SAVINGS_GOAL).length,
  };
}

/** The landing always shows this many months; a shorter history leaves the first slots empty. */
export const SAVINGS_SLOTS = 6;

/** The `count` months before a `YYYY-MM` period, oldest first. */
export function monthsBefore(month: string, count: number): string[] {
  const [year, m] = month.split('-').map(Number);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(year, m - 1 - (count - i), 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}
